import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2, RefreshCw, ScrollText, ShieldCheck, ShieldAlert } from "lucide-react";
import type { SalesLedgerEntry, SalesLedgerKind } from "@wedding/shared";
import { adminLedgerApi } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Satış & Site → "Kayıt defteri" (migration 057). Fiyat/koşul ayarlarının,
 * paket limitlerinin, sipariş ve ek paket olaylarının YALNIZ EKLENEN kaydı.
 * "Hangi tarihte hangi fiyat geçerliydi" ve "alıcı hangi metni onayladı"
 * buradan gösterilir; "Bütünlüğü doğrula" zinciri baştan yeniden hesaplar.
 */

const FILTERS: Array<SalesLedgerKind | "all"> = ["all", "settings", "plan", "order", "addon"];

const SETTINGS_LABEL: Record<string, string> = {
  "site:pricing": "siteAdmin.tabs.pricing",
  "site:addons": "siteAdmin.addons.title",
  "site:legal": "siteAdmin.tabs.legal",
  "site:retention": "siteAdmin.tabs.retention",
  "site:signup": "siteAdmin.tabs.sales",
};

const EVENT_KEYS: Record<string, string> = {
  created: "siteAdmin.ledger.events.created",
  paid: "siteAdmin.ledger.events.paid",
  cancelled: "siteAdmin.ledger.events.cancelled",
  moved: "siteAdmin.ledger.events.moved",
  corrected: "siteAdmin.ledger.events.corrected",
  applied: "siteAdmin.ledger.events.applied",
  updated: "siteAdmin.ledger.events.updated",
  deleted: "siteAdmin.ledger.events.deleted",
  baseline: "siteAdmin.ledger.events.baseline",
  account_plan: "siteAdmin.ledger.events.accountPlan",
  // Paket modeli (2026-10-16): Free / geçiş paketi olayları (`package:<id>`).
  granted: "siteAdmin.ledger.events.granted",
  assigned: "siteAdmin.ledger.events.assigned",
  replaced: "siteAdmin.ledger.events.replaced",
  // Finansal denetim (2026-10-16)
  revoked: "siteAdmin.ledger.events.revoked",
  noted: "siteAdmin.ledger.events.noted",
  propagated: "siteAdmin.ledger.events.propagated",
};

type Body = {
  event?: string;
  before?: unknown;
  after?: unknown;
  number?: string;
  order?: { number?: string };
  value?: unknown;
  row?: { slug?: string; label?: string };
  kind?: string;
  label?: string;
  documents?: { text: string; hash: string } | null;
};

function parseBody(e: SalesLedgerEntry): Body {
  try {
    return JSON.parse(e.body) as Body;
  } catch {
    return {};
  }
}

export function LedgerTab({ showToast }: { showToast: (msg: string, ok?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<SalesLedgerKind | "all">("all");
  const [items, setItems] = useState<SalesLedgerEntry[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [next, setNext] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verdict, setVerdict] = useState<{ ok: boolean; count: number; brokenAt: number | null } | null>(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const res = await adminLedgerApi.list({ kind: filter === "all" ? undefined : filter });
      setAvailable(res.data?.available ?? true);
      setItems(res.data?.items ?? []);
      setNext(res.data?.nextCursor ?? null);
    } catch (err) {
      setItems([]);
      showToast(t("siteAdmin.ledger.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    }
  }, [filter, showToast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const res = await adminLedgerApi.list({ kind: filter === "all" ? undefined : filter, before: next });
      setItems((cur) => [...(cur ?? []), ...(res.data?.items ?? [])]);
      setNext(res.data?.nextCursor ?? null);
    } catch (err) {
      showToast(t("siteAdmin.ledger.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setLoadingMore(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const res = await adminLedgerApi.verify();
      const d = res.data;
      if (!d?.available) {
        showToast(t("siteAdmin.ledger.unavailable"), false);
        return;
      }
      setVerdict({ ok: d.ok, count: d.count, brokenAt: d.brokenAt });
      if (d.ok) showToast(t("siteAdmin.ledger.verifyOk", { count: d.count }));
      else showToast(t("siteAdmin.ledger.verifyBroken", { seq: d.brokenAt }), false);
    } catch (err) {
      showToast(t("siteAdmin.ledger.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setVerifying(false);
    }
  };

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  };

  const subject = (e: SalesLedgerEntry, b: Body) => {
    if (e.kind === "settings") return SETTINGS_LABEL[e.ref] ? t(SETTINGS_LABEL[e.ref]) : e.ref;
    if (e.kind === "plan" && b.event === "account_plan") {
      return t("siteAdmin.ledger.accountPlanSubject", { before: String(b.before ?? "—"), after: String(b.after ?? "—"), id: e.ref.slice(8, 16) });
    }
    if (e.kind === "plan" && e.ref.startsWith("package:")) {
      return t("siteAdmin.ledger.packageSubject", {
        kind: t(b.kind === "legacy" ? "siteAdmin.ledger.packageLegacy" : "siteAdmin.ledger.packageFree"),
        label: b.label ?? "",
        id: e.ref.slice(8, 20),
      });
    }
    if (e.kind === "plan") return b.row?.label ? `${b.row.label} (${e.ref})` : e.ref;
    if (e.kind === "order") return b.number ?? b.order?.number ?? e.ref.slice(0, 8);
    return e.ref.slice(0, 8);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ScrollText className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{t("siteAdmin.ledger.title")}</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.ledger.desc")}</p>
          </div>
          <button
            type="button"
            onClick={() => void verify()}
            disabled={verifying || !available}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}
            <span>{t("siteAdmin.ledger.verify")}</span>
          </button>
        </div>

        {verdict && (
          <p
            role="status"
            data-testid="ledger-verdict"
            className={cn(
              "mx-5 mt-4 flex items-center gap-2 rounded-xl px-3 py-2 text-sm",
              verdict.ok ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-red-500/10 text-red-700 dark:text-red-300",
            )}
          >
            {verdict.ok ? <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" /> : <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            <span>{verdict.ok ? t("siteAdmin.ledger.verifyOk", { count: verdict.count }) : t("siteAdmin.ledger.verifyBroken", { seq: verdict.brokenAt })}</span>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 px-5 pt-4">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium cursor-pointer",
                filter === f ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`siteAdmin.ledger.filter.${f}`)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void load()}
            aria-label={t("siteAdmin.reload")}
            className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 py-4">
          {!available ? (
            <p data-testid="ledger-unavailable" className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              {t("siteAdmin.ledger.unavailable")}
            </p>
          ) : items === null ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("siteAdmin.ledger.empty")}</p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border">
              {items.map((e) => {
                const b = parseBody(e);
                const isOpen = open === e.seq;
                return (
                  <li key={e.seq} data-testid={`ledger-${e.seq}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : e.seq)}
                      aria-expanded={isOpen}
                      className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left text-sm hover:bg-primary/5 cursor-pointer"
                    >
                      <span className="tabular-nums text-xs text-muted-foreground">#{e.seq}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{t(`siteAdmin.ledger.filter.${e.kind}`)}</span>
                      <span className="font-medium text-foreground">{subject(e, b)}</span>
                      {b.event && EVENT_KEYS[b.event] && <span className="text-xs text-muted-foreground">{t(EVENT_KEYS[b.event])}</span>}
                      {e.actor === "baseline" && <span className="text-xs text-muted-foreground">{t("siteAdmin.ledger.events.baseline")}</span>}
                      <span className="ml-auto text-xs text-muted-foreground tabular-nums">{fmt(e.created_at)}</span>
                      <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <div className="space-y-2 px-4 pb-4 text-xs">
                        <p className="text-muted-foreground">
                          {t("siteAdmin.ledger.actor")}: <span className="text-foreground">{e.actor}</span> ·{" "}
                          <span className="break-all font-mono">{e.hash}</span>
                        </p>
                        {b.documents ? (
                          <>
                            <p className="font-medium text-foreground">{t("siteAdmin.ledger.contract", { hash: b.documents.hash })}</p>
                            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-xl bg-muted/40 p-3 font-sans text-[12px] leading-relaxed">{b.documents.text}</pre>
                          </>
                        ) : (
                          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-muted/40 p-3 font-mono text-[11px]">
                            {JSON.stringify(b, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {next && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void more()}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-primary/5 disabled:opacity-40 cursor-pointer"
              >
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                <span>{t("siteAdmin.ledger.more")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
